import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { globalPropTypes } from 'helpers';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import DebouncedSelect from '../../../../components/DebounceSelect/DebounceSelect';
import {
  holderAuthorizeOptions,
  statusOptions,
  titleOptions,
  typeOptions,
  visibilityOptions,
} from '../../../../shared/Cis/CisOptions';
import { getCertificateHoldersOptions } from '../../../../api/services/cis';
import { toApiDate } from 'helpers/date';

const EditUnknownImport = ({
  state,
  dispatch,
  updateFieldCreator,
  setEditDataCreator,
  unknownImport,
  errors,
  updateUnknownImport,
  removeError,
  levelDomains,
  certificationBodies,
  loadCertificatesAttributes,
  organisations,
  loadClientMemberAssociations,
  memberAssociations,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    if (unknownImport) {
      dispatch(setEditDataCreator(unknownImport));
    }
  }, [unknownImport]);

  useEffect(() => {
    if (state.certificationBodyId) {
      loadClientMemberAssociations(state.certificationBodyId);
    }
  }, [state.certificationBodyId]);

  useEffect(() => {
    loadCertificatesAttributes();
  }, []);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const submit = () => {
    const params = {
      holderId: state.holderId ?? null,
      title: state.title,
      firstName: state.firstName,
      middleName: state.middleName,
      lastName: state.lastName,
      email: state.email,
      birthDate: state.birthDate ? toApiDate(state.birthDate) : null,
      status: state.status,
      levelId: state.levelId ?? null,
      issueDate: state.issueDate ? toApiDate(state.issueDate) : null,
      maId: state.maId ?? null,
      certType: state.certType,
      validUntil: state.validUntil ? toApiDate(state.validUntil) : null,
      certificateNumber: state.certificateNumber,
      authorizedByHolder: state.authorizedByHolder,
      visibility: state.visibility,
      organisation: state.organisation ?? null,
      certificationBodyId: state.certificationBodyId ?? null,
    };

    updateUnknownImport(state.id, params, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.certificates.editCertificate')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="holderId"
                  label={t('page.user.cis.unknownImports.holderId')}
                  error={errors.holderId}
                >
                  <DebouncedSelect
                    id="holderId"
                    value={state.holderId}
                    onChange={handleChangeSelect('holderId', 'holderId')}
                    placeholder={t('page.user.cis.unknownImports.holderIdPlaceholder')}
                    fetchOptions={getCertificateHoldersOptions}
                    intialSelectedOption={state.selectedHolder}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="title"
                  label={t('page.user.cis.unknownImports.title')}
                  error={errors.title}
                >
                  <Select
                    id="title"
                    placeholder={t('page.user.cis.certificateHolders.titlePlaceholder')}
                    value={titleOptions.find((option) => option.value === state.title) || ''}
                    options={titleOptions}
                    onChange={handleChangeSelect('title', 'title')}
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="email"
                  label={t('page.user.cis.unknownImports.email')}
                  error={errors.email}
                >
                  <input
                    id="email"
                    type="email"
                    value={state.email || ''}
                    onChange={handleChangeNative('email', 'email')}
                    placeholder={t('page.user.cis.unknownImports.emailPlaceholder')}
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="firstName"
                  label={t('page.user.cis.unknownImports.firstName')}
                  error={errors.firstName}
                >
                  <input
                    id="firstName"
                    type="text"
                    value={state.firstName || ''}
                    onChange={handleChangeNative('firstName', 'firstName')}
                    placeholder={t('page.user.cis.unknownImports.firstNamePlaceholder')}
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="middleName"
                  label={t('page.user.cis.unknownImports.middleName')}
                  error={errors.middleName}
                >
                  <input
                    id="middleName"
                    type="text"
                    value={state.middleName || ''}
                    onChange={handleChangeNative('middleName', 'middleName')}
                    placeholder={t('page.user.cis.unknownImports.middleNamePlaceholder')}
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="lastName"
                  label={t('page.user.cis.unknownImports.lastName')}
                  error={errors.lastName}
                >
                  <input
                    id="lastName"
                    type="text"
                    value={state.lastName || ''}
                    onChange={handleChangeNative('lastName', 'lastName')}
                    placeholder={t('page.user.cis.unknownImports.lastNamePlaceholder')}
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="birthDate"
                  label={t('page.user.cis.unknownImports.birthDate')}
                  value={state.birthDate}
                  clear={() => handleChangeCustom('birthDate')(null)}
                  error={errors.birthDate}
                >
                  <Datepicker
                    id="birthDate"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.unknownImports.birthDatePlaceholder')}
                    selected={state.birthDate}
                    onChange={handleChangeCustom('birthDate', 'birthDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="status"
                  label={t('page.user.cis.unknownImports.status')}
                  error={errors.status}
                >
                  <Select
                    id="status"
                    value={statusOptions.find((option) => option.value === state.status) || ''}
                    options={statusOptions}
                    onChange={handleChangeSelect('status', 'status')}
                    placeholder={t('page.user.cis.unknownImports.statusPlaceholder')}
                    disabled={!!state.holderId}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Margin bottom="24">
            <Heading size="h5">
              {t('page.user.cis.certificateHolders.editCertificateHolder')}
            </Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="levelId"
                  label={t('page.user.cis.unknownImports.levelId')}
                  error={errors.levelId}
                  value={levelDomains.find((option) => option.value === state.levelId) || ''}
                  clear={() => handleChangeSelect('levelId')(null)}
                  required
                >
                  <Select
                    id="levelId"
                    type="text"
                    placeholder={t('page.user.cis.unknownImports.levelIdPlaceholder')}
                    value={levelDomains.find((option) => option.value === state.levelId) || ''}
                    options={levelDomains}
                    onChange={handleChangeSelect('levelId', 'levelId')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="issueDate"
                  label={t('page.user.cis.unknownImports.issueDate')}
                  value={state.issueDate}
                  clear={() => handleChangeCustom('issueDate')(null)}
                  required
                  error={errors.issueDate}
                >
                  <Datepicker
                    id="issueDate"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.unknownImports.issueDatePlaceholder')}
                    selected={state.issueDate}
                    onChange={handleChangeCustom('issueDate', 'issueDate')}
                    dateFormat="yyyy-MM-dd"
                    maxDate={new Date()}
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validUntil"
                  label={t('page.user.cis.unknownImports.validUntil')}
                  value={state.validUntil}
                  clear={() => handleChangeCustom('validUntil')(null)}
                  required
                  error={errors.validUntil}
                >
                  <Datepicker
                    id="validUntil"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.unknownImports.issueDatePlaceholder')}
                    selected={state.validUntil}
                    onChange={handleChangeCustom('validUntil', 'validUntil')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    minDate={state.issueDate}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certificationBodyId"
                  label={t('page.user.cis.unknownImports.certificationBody')}
                  value={
                    certificationBodies.find(
                      (option) => option.value === state.certificationBodyId,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('certificationBodyId')(null)}
                  required
                  error={errors.certificationBodyId}
                >
                  <Select
                    id="certificationBodyId"
                    type="text"
                    placeholder={t('page.user.cis.unknownImports.certificationBodyPlaceholder')}
                    value={
                      certificationBodies.find(
                        (option) => option.value === state.certificationBodyId,
                      ) || ''
                    }
                    options={certificationBodies}
                    onChange={handleChangeSelect('certificationBodyId', 'certificationBodyId')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="maId"
                  label={t('page.user.cis.certificates.clientMemberAssociation')}
                  value={
                    memberAssociations.find((option) => option.value === state.memberAssociation) ||
                    ''
                  }
                  required
                  clear={() => handleChangeSelect('maId')(null)}
                  error={errors.maId}
                >
                  <Select
                    id="maId"
                    type="text"
                    placeholder={t(
                      'page.user.cis.unknownImports.clientMemberAssociationPlaceholder',
                    )}
                    value={memberAssociations.find((option) => option.value === state.maId) || ''}
                    options={memberAssociations}
                    onChange={handleChangeSelect('maId', 'maId')}
                    disabled={!memberAssociations || memberAssociations.length === 0}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="organisation"
                  label={t('page.user.cis.unknownImports.globalCustomerName')}
                  value={organisations.find((option) => option.value === state.organisation) || ''}
                  clear={() => handleChangeSelect('organisation')(null)}
                  error={errors.organisation}
                >
                  <Select
                    id="organisation"
                    type="text"
                    placeholder={t('page.user.cis.unknownImports.globalCustomerNamePlaceholder')}
                    value={
                      organisations.find((option) => option.value === state.organisation) || ''
                    }
                    options={organisations}
                    onChange={handleChangeSelect('organisation', 'organisation')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certType"
                  label={t('page.user.cis.unknownImports.certType')}
                  value={typeOptions.find((option) => option.value === state.certType) || ''}
                  clear={() => handleChangeSelect('certType')(null)}
                  error={errors.certType}
                >
                  <Select
                    id="certType"
                    type="text"
                    placeholder={t('page.user.cis.unknownImports.certTypePlaceholder')}
                    value={typeOptions.find((option) => option.value === state.certType) || ''}
                    options={typeOptions}
                    onChange={handleChangeSelect('certType', 'certType')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certificateNumber"
                  label={t('page.user.cis.unknownImports.certificateNumber')}
                  error={errors.certificateNumber}
                >
                  <input
                    id="certificateNumber"
                    type="text"
                    value={state.certificateNumber || ''}
                    onChange={handleChangeNative('certificateNumber', 'certificateNumber')}
                    placeholder={t('page.user.cis.unknownImports.certificateNumberPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="authorizedByHolder"
                  label={t('page.user.cis.unknownImports.holderAuthorized')}
                  value={
                    holderAuthorizeOptions.find(
                      (option) => option.value === state.authorizedByHolder,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('authorizedByHolder')(null)}
                  error={errors.authorizedByHolder}
                >
                  <Select
                    id="authorizedByHolder"
                    type="text"
                    placeholder={t('page.user.cis.unknownImports.holderAuthorizedPlaceholder')}
                    value={
                      holderAuthorizeOptions.find(
                        (option) => option.value === state.authorizedByHolder,
                      ) || ''
                    }
                    options={holderAuthorizeOptions}
                    onChange={handleChangeSelect('authorizedByHolder', 'authorizedByHolder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="visibility"
                  label={t('page.user.cis.unknownImports.visibility')}
                  value={
                    visibilityOptions.find((option) => option.value === state.visibility) || ''
                  }
                  clear={() => handleChangeSelect('visibility')(null)}
                  error={errors.visibility}
                >
                  <Select
                    id="visibility"
                    type="text"
                    placeholder={t('page.user.cis.unknownImports.visibilityPlaceholder')}
                    value={
                      visibilityOptions.find((option) => option.value === state.visibility) || ''
                    }
                    options={visibilityOptions}
                    onChange={handleChangeSelect('visibility', 'visibility')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditUnknownImport.propTypes = {
  unknownImport: PropTypes.shape({
    holderId: PropTypes.number,
    title: PropTypes.string,
    firstName: PropTypes.string,
    middleName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    birthDate: PropTypes.any,
    status: PropTypes.number,
    levelId: PropTypes.number,
    issueDate: PropTypes.any,
    maId: PropTypes.number,
    certType: PropTypes.string,
    validUntil: PropTypes.any,
    certificateNumber: PropTypes.string,
    authorizedByHolder: PropTypes.bool,
    visibility: PropTypes.bool,
    organisation: PropTypes.number,
    certificationBodyId: PropTypes.number,
  }),
  errors: PropTypes.object,
  updateUnknownImport: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadUnknownImportAttributes: PropTypes.func.isRequired,
};

EditUnknownImport.defaultProps = {
  unknownImport: {},
  errors: {},
};

export default EditUnknownImport;
